import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Chip,
  Paper,
  LinearProgress,
  Stepper,
  Step,
  StepButton,
  StepLabel,
  Autocomplete,
  TextField,
  Button,
  CircularProgress,
  useTheme,
} from "@mui/material";
import {
  ReceiptLong,
  CalendarToday,
  AttachMoney,
  Description,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import { Formik, Form, Field } from "formik";
import * as yup from "yup";

import { generateAndExecuteMutation, graphqlQuery } from "../../../data/Axios/DynamicService";
import { HRService } from "../../../config";
import { approvedDisbursedImprests, allImprestSurrender } from "../../../data/Queries/HRmanagement/leavemanagement";
import { tokens } from "../../../theme";

const base_url = HRService.uri;

// ---------------------------------------------------------------------
// SurrenderLineForm – ONE-PAGE, NO DynamicForm
// ---------------------------------------------------------------------
const SurrenderLineForm = ({ data, isEditing, onClose, onAction }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id, 10);
  const companyId = parseInt(decodedToken.CompanyId, 10);

  const surrenderData = JSON.parse(localStorage.getItem("SurrenderId") || "{}");
  const surrenderId = surrenderData?.id;

  // -----------------------------------------------------------------
  // State
  // -----------------------------------------------------------------
  const [surrender, setSurrender] = useState(null);
  const [imprest, setImprest] = useState(null);
  const [lines, setLines] = useState([]);
  const [selectedLine, setSelectedLine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  // -----------------------------------------------------------------
  // 1. Fetch Surrender and Imprest Lines
  // -----------------------------------------------------------------
  useEffect(() => {
    const fetchData = async () => {
      if (!surrenderId) {
        setLoading(false);
        return;
      }
      try {
        const surrendersResult = await graphqlQuery(allImprestSurrender, base_url);
        const currentSurrender = surrendersResult.find((s) => s.id === surrenderId);
        setSurrender(currentSurrender);

        if (currentSurrender?.applicationId) {
          const imprestsResult = await graphqlQuery(approvedDisbursedImprests, base_url);
          const currentImprest = imprestsResult.find((app) => app.id === currentSurrender.applicationId);
          setImprest(currentImprest);

          // Assuming the imprest object has a 'lines' array with line details
          // If not, adjust the query to include lines
          const availableLines = (currentImprest?.lines || []).filter(
            (line) => !currentSurrender.lines?.some((sl) => sl.applicationLineId === line.id) // Avoid already surrendered lines
          );
          setLines(availableLines);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [surrenderId]);

  // -----------------------------------------------------------------
  // 2. Line Options for Dropdown
  // -----------------------------------------------------------------
  const lineOptions = useMemo(
    () =>
      lines.map((line) => ({
        value: line.id,
        label: `${line.description || "No description"} (#${line.id}) – ${imprest?.currencyCode} ${line.amount?.toFixed(2)}`,
        line, // Full line data
      })),
    [lines, imprest]
  );

  // -----------------------------------------------------------------
  // 3. Formik schema & initial values
  // -----------------------------------------------------------------
  const validationSchema = yup.object({
    applicationLineId: yup.number().required("Please select an imprest line"),
    description: yup.string().required("Description is required"),
    amountSpent: yup
      .number()
      .required("Please enter the amount spent")
      .min(0, "Amount spent must be greater than or equal to 0"),
  });

  const initialValues = {
    id: data.id ?? null,
    applicationLineId: data?.applicationLineId ?? "",
    description: data?.description ?? "",
    amountSpent: data?.amountSpent ?? 0,
    document: data?.document ?? null,
  };

  // -----------------------------------------------------------------
  // 4. Stepper definition (visual only – one step)
  // -----------------------------------------------------------------
  const steps = [
    {
      label: "Surrender Line",
      description: "Select the imprest line and enter surrender details",
      icon: "receipt_long",
    },
  ];

  const progress = ((activeStep + 1) / steps.length) * 100;

  // -----------------------------------------------------------------
  // 5. Submit
  // -----------------------------------------------------------------
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        id : isEditing? data.id : null,
        amountSpent: parseFloat(values.amountSpent),
        isActive: 1
        // Note: Handle file upload separately (e.g., via FormData or base64) if needed
        // document: values.document ? ... : null,
      };
      const mutationName = isEditing ? "updateImprestSurrenderLine" : "addImprestSurrenderLine";

      const response = await generateAndExecuteMutation(
        mutationName,
        payload,
        "imprestSurrenderLine",
        base_url
      );

      if (response?.[mutationName]) {
        onClose?.();
        onAction?.();
        Swal.fire({ icon: "success", title: "Success!", text: "Imprest surrender line saved successfully." });
      }
    } catch (error) {
      console.error("Submission error:", error);
      Swal.fire({ icon: "error", title: "Error!", text: error.message || "Failed to save surrender line." });
    } finally {
      setSubmitting(false);
    }
  };

  // -----------------------------------------------------------------
  // 6. Handle Edit Prefill for Selected Line
  // -----------------------------------------------------------------
  useEffect(() => {
    if (data?.applicationLineId && lines.length > 0) {
      const existingLine = lines.find((l) => l.id === data.applicationLineId);
      if (existingLine) {
        setSelectedLine(existingLine);
      }
    }
  }, [data, lines]);

  // -----------------------------------------------------------------
  // 7. Loading UI
  // -----------------------------------------------------------------
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  // -----------------------------------------------------------------
  // 8. Render
  // -----------------------------------------------------------------
  return (
    <Box m="20px">
      {/* Header */}
      <Typography variant="h4" fontWeight={600} color={colors.grey[100]} gutterBottom>
        {isEditing ? "Update Surrender Line" : "Add Imprest Surrender Line"}
      </Typography>
      <Typography variant="body1" color={colors.grey[300]} mb={3}>
        Select the imprest line to surrender and provide details.
      </Typography>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue, isSubmitting }) => {
          // Determine line data for preview (selected or from data for edit)
          let lineDataForPreview = selectedLine;
          if (isEditing && data && !selectedLine) {
            lineDataForPreview = {
              ...data,
              amount: data.amountRequested,
              // Merge with original line if needed
            };
          }

          const requestedDate = lineDataForPreview?.requestedDate
            ? new Date(lineDataForPreview.requestedDate).toLocaleDateString()
            : new Date().toLocaleDateString(); // Default to today

          return (
            <Form>
              {/* Form Fields - Line Selection */}
              {/* <Paper elevation={2} sx={{ p: 3, mb: 3, minHeight: 100, bgcolor: colors.primary[400] }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Autocomplete
                      options={lineOptions}
                      getOptionLabel={(opt) => opt.label}
                      value={lineOptions.find((o) => o.value === values.applicationLineId) || null}
                      onChange={(_, newVal) => {
                        setFieldValue("applicationLineId", newVal?.value ?? "");
                        if (newVal) {
                          const lineOpt = lineOptions.find((o) => o.value === newVal.value);
                          if (lineOpt) {
                            setSelectedLine(lineOpt.line);
                            if (!isEditing) {
                              setFieldValue("description", lineOpt.line.description || "");
                            }
                          }
                        } else {
                          setSelectedLine(null);
                        }
                        setCompletedSteps((s) => new Set([...s, activeStep]));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Imprest Line"
                          variant="filled"
                          error={touched.applicationLineId && !!errors.applicationLineId}
                          helperText={(touched.applicationLineId && errors.applicationLineId) || " "}
                          InputProps={{
                            ...params.InputProps,
                            style: { color: colors.grey[100] },
                          }}
                          InputLabelProps={{ style: { color: colors.grey[300] } }}
                          sx={{
                            "& .MuiFormHelperText-root": { color: colors.grey[300] },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Paper> */}

              {/* Preview Card - Line Details */}
              {(lineDataForPreview || isEditing) && (
                <Card variant="outlined" sx={{ mt: 3, mb: 3, bgcolor: colors.primary[500] }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ bgcolor: colors.greenAccent[700] }} gutterBottom>
                      <ReceiptLong sx={{ verticalAlign: "middle", mr: 1 }} />
                      Surrender Line Details
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Description:</strong>
                        </Typography>
                        <Typography variant="body1">{lineDataForPreview?.description || data?.description || "N/A"}</Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Account Id:</strong>
                        </Typography>
                        <Typography variant="body1">{lineDataForPreview?.accountId || data?.accountId || "N/A"}</Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Amount Requested:</strong>
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" color="success.main">
                          <AttachMoney fontSize="small" />
                          {(lineDataForPreview?.amount || data?.amountRequested || 0).toFixed(2)} {imprest?.currencyCode}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Requested Date:</strong>
                        </Typography>
                        <Typography variant="body1">
                          <CalendarToday fontSize="small" sx={{ mr: 0.5 }} />
                          {requestedDate}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Amount Spent and Document Upload Box */}
              <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: colors.primary[400] }}>
                <Typography variant="h6" color={colors.grey[100]} gutterBottom>
                  Insert the amount spent and upload the supporting documents
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Field
                      name="amountSpent"
                      as={TextField}
                      type="number"
                      label="Amount Spent"
                      variant="filled"
                      fullWidth
                      InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                      error={touched.amountSpent && !!errors.amountSpent}
                      helperText={touched.amountSpent && errors.amountSpent}
                      InputLabelProps={{ style: { color: colors.grey[300] } }}
                      sx={{
                        "& .MuiInputBase-input": { color: colors.grey[100] },
                        "& .MuiFormHelperText-root": { color: colors.grey[300] },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="body2" color={colors.grey[300]} gutterBottom>
                        Upload Supporting Document
                      </Typography>
                      <input
                        id="document-upload"
                        name="document"
                        type="file"
                        onChange={(event) => {
                          setFieldValue("document", event.currentTarget.files?.[0] || null);
                        }}
                        style={{ display: "none" }}
                      />
                      <label htmlFor="document-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<Description />}
                          sx={{
                            borderColor: colors.grey[400],
                            color: colors.grey[100],
                            "&:hover": { borderColor: colors.greenAccent[500] },
                          }}
                        >
                          {values.document ? values.document.name : "Choose File"}
                        </Button>
                      </label>
                      {touched.document && errors.document && (
                        <Typography variant="caption" color="error.main" sx={{ display: "block", mt: 1 }}>
                          {errors.document}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Sticky Navigation */}
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  position: "sticky",
                  bottom: 0,
                  bgcolor: colors.primary[400],
                  zIndex: 10,
                }}
              >
                <Button
                  disabled={activeStep === 0}
                  onClick={() => setActiveStep((p) => Math.max(p - 1, 0))}
                  startIcon={<BackIcon />}
                  size="large"
                  variant="outlined"
                  sx={{
                    borderColor: colors.grey[400],
                    color: colors.grey[100],
                    "&:hover": { borderColor: colors.greenAccent[500], bgcolor: colors.primary[500] },
                  }}
                >
                  Back
                </Button>

                <Typography variant="body2" color={colors.grey[300]}>
                  {Object.keys(errors).length > 0 && (
                    <span style={{ color: colors.redAccent[500] }}>Please fix errors before submitting</span>
                  )}
                </Typography>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || !values.applicationLineId}
                  size="large"
                  startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
                  sx={{
                    minWidth: 150,
                    fontWeight: 600,
                    bgcolor: colors.greenAccent[500],
                    color: colors.grey[900],
                    "&:hover": { bgcolor: colors.greenAccent[600] },
                  }}
                >
                  {isSubmitting ? "Saving…" : isEditing ? "Update" : "Add Line"}
                </Button>
              </Paper>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default SurrenderLineForm;